import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../auth/AuthContext";
import { toast } from "sonner";
import { Camera, Check, Loader, RotateCcw } from "lucide-react";
import { inspectionPhotosComplete } from "../components/VehicleInspectionCapture";
import { PARKING_PHOTO_ANGLES, parkingPhotosComplete } from "../lib/parkingPhotos";
import { compressImageForUpload, formatUploadError } from "../lib/compressImage";
import { formatInspectionSessionError, formatInspectionSubmitError } from "../lib/bookingErrors";
import { displayOdometerKm } from "../lib/kmUnits";
import { photoEntryFromApi } from "../lib/fileUrl";

const ANGLES = [
  { id: "front", label: "Front", hint: "Full front bumper and hood" },
  { id: "rear", label: "Rear", hint: "Full rear bumper and trunk" },
  { id: "driver_side", label: "Driver side", hint: "Full left side profile" },
  { id: "passenger_side", label: "Passenger side", hint: "Full right side profile" },
  { id: "interior", label: "Interior", hint: "Dashboard and front seats" },
  { id: "odometer", label: "Odometer", hint: "Clear odometer reading in km" },
];

const PHASE_LABELS = {
  listing_baseline: "Listing condition scan",
  pickup: "Pickup inspection",
  return: "Return inspection",
  parking_listing: "Parking photos",
};

function anglesForSession(session) {
  if (session?.phase === "parking_listing") return PARKING_PHOTO_ANGLES;
  return ANGLES;
}

function photosCompleteForSession(session, photos) {
  if (session?.phase === "parking_listing") return parkingPhotosComplete(photos);
  return inspectionPhotosComplete(photos);
}

function withFullUrl(entry) {
  return photoEntryFromApi(entry);
}

export default function MobileInspect() {
  const { token } = useParams();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [photos, setPhotos] = useState({});
  const [activeAngle, setActiveAngle] = useState(ANGLES[0].id);
  const [busy, setBusy] = useState(false);
  const [odometer, setOdometer] = useState("");
  const [fuel, setFuel] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);
  const pollCount = useRef(0);
  const INITIAL_LOAD_TIMEOUT_MS = 15000;

  const load = async ({ isInitial = false } = {}) => {
    try {
      const r = await axios.get(`${API}/inspections/qr-sessions/${token}`, {
        timeout: isInitial ? INITIAL_LOAD_TIMEOUT_MS : undefined,
      });
      setSession(r.data);
      setError(null);
      const draft = {};
      for (const [angle, entry] of Object.entries(r.data.draft_photos || {})) {
        const full = withFullUrl(entry);
        if (full) draft[angle] = full;
      }
      setPhotos(draft);
      const odoKm = displayOdometerKm(r.data);
      if (odoKm != null) setOdometer(String(odoKm));
      if (r.data.fuel_pct != null) setFuel(String(r.data.fuel_pct));
      if (r.data.notes) setNotes(r.data.notes);
      const firstMissing = anglesForSession(r.data).find(a => !draft[a.id]);
      if (firstMissing) setActiveAngle(firstMissing.id);
    } catch (e) {
      const timedOut = e.code === "ECONNABORTED";
      setError(
        timedOut
          ? "Session took too long to load — check your connection and try again."
          : formatInspectionSessionError(e.response?.data?.detail) || "Session not found or expired"
      );
    } finally {
      if (isInitial) setLoading(false);
    }
  };

  useEffect(() => {
    pollCount.current = 0;
    setLoading(true);
    load({ isInitial: true });
  }, [token]);

  useEffect(() => {
    if (!token || session?.status === "submitted") return;
    pollCount.current = 0;
    const id = setInterval(() => {
      if (pollCount.current >= 100) return;
      pollCount.current += 1;
      load();
    }, 3000);
    return () => clearInterval(id);
  }, [token, session?.status]);

  const uploadFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const compressed = await compressImageForUpload(file);
      const fd = new FormData();
      fd.append("file", compressed, compressed.name || `inspect-${activeAngle}.jpg`);
      const r = await axios.post(
        `${API}/inspections/qr-sessions/${token}/upload?angle=${activeAngle}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const entry = withFullUrl(r.data);
      setPhotos(prev => ({ ...prev, [activeAngle]: entry }));
      toast.success(`${anglesForSession(session).find(a => a.id === activeAngle)?.label} saved`);
      const slotAngles = anglesForSession(session);
      const next = slotAngles.find(a => !photos[a.id] && a.id !== activeAngle);
      if (next) setActiveAngle(next.id);
    } catch (e) {
      toast.error(formatUploadError(e.response?.data?.detail) || e.message || "Upload failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async () => {
    if (!photosCompleteForSession(session, photos)) {
      toast.error(session?.phase === "parking_listing" ? "Add at least one parking photo" : "Capture all 6 angles first");
      return;
    }
    if (session?.phase === "parking_listing") {
      toast.success("Photos saved — return to your browser to submit the listing");
      await load();
      return;
    }
    const isPickup = session?.phase === "pickup";
    const odoVal = odometer ? parseInt(odometer, 10) : NaN;
    if (!Number.isFinite(odoVal) || odoVal < 0) {
      toast.error("Enter the odometer reading in km");
      return;
    }
    if (isPickup) {
      const fuelVal = fuel ? parseInt(fuel, 10) : NaN;
      if (!Number.isFinite(fuelVal) || fuelVal < 0 || fuelVal > 100) {
        toast.error("Enter fuel or battery charge % (0–100) before pickup");
        return;
      }
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inspections/qr-sessions/${token}/submit`, {
        odometer_km: odoVal,
        fuel_pct: fuel ? parseInt(fuel, 10) : undefined,
        notes,
      });
      toast.success("Inspection submitted — you can close this page");
      await load();
    } catch (e) {
      toast.error(formatInspectionSubmitError(e.response?.data?.detail) || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center" data-testid="mobile-inspect-loading">
        <Loader size={24} className="animate-spin text-[var(--zc-text-3)]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-md mx-auto px-5 py-16 text-center" data-testid="mobile-inspect-error">
        <h1 className="zc-headline text-2xl">Session unavailable</h1>
        <p className="text-sm text-[var(--zc-text-2)] mt-3">{error}</p>
        <p className="text-xs text-[var(--zc-text-3)] mt-4">Scan a fresh QR code from the Z-Cars web portal to try again.</p>
      </div>
    );
  }

  if (session?.status === "submitted") {
    return (
      <div className="max-w-md mx-auto px-5 py-16 text-center" data-testid="mobile-inspect-done">
        <div className="w-14 h-14 rounded-full bg-[var(--zc-success-soft,#dcfce7)] flex items-center justify-center mx-auto">
          <Check size={28} className="text-[var(--zc-success,#16a34a)]" />
        </div>
        <h1 className="zc-headline text-2xl mt-5">Inspection complete</h1>
        <p className="text-sm text-[var(--zc-text-2)] mt-2">
          {PHASE_LABELS[session.phase] || "Inspection"} submitted successfully. Return to your browser to continue.
        </p>
      </div>
    );
  }

  const slotAngles = anglesForSession(session);
  const current = slotAngles.find(a => a.id === activeAngle);
  const doneCount = slotAngles.filter(a => photos[a.id]).length;
  const showTripFields = session?.phase === "pickup" || session?.phase === "return";
  const isPickup = session?.phase === "pickup";
  const isListingDraft = session?.phase === "listing_baseline" || session?.phase === "parking_listing";
  const displayLabel = session?.parking_label || session?.vehicle_label;

  return (
    <div className="max-w-md mx-auto px-4 py-8 pb-24" data-testid="mobile-inspect-page">
      <div className="eyebrow">Z-Cars mobile scan</div>
      <h1 className="zc-headline text-2xl mt-1">{PHASE_LABELS[session?.phase] || "Condition scan"}</h1>
      {displayLabel && (
        <p className="text-sm text-[var(--zc-text-2)] mt-1">{displayLabel}</p>
      )}

      <div className="flex items-center gap-2 mt-4 mb-4">
        <div className="text-xs text-[var(--zc-text-3)]">{doneCount}/{slotAngles.length} {session?.phase === "parking_listing" ? "photos" : "angles"}</div>
        <div className="flex-1 h-1.5 bg-[var(--zc-bg-3)] rounded-full overflow-hidden">
          <div className="h-full bg-[var(--zc-accent)] transition-all" style={{ width: `${(doneCount / slotAngles.length) * 100}%` }} />
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-4">
        {slotAngles.map(a => (
          <button
            key={a.id}
            type="button"
            onClick={() => setActiveAngle(a.id)}
            className={"px-2.5 h-7 text-caption rounded-full border font-semibold " +
              (activeAngle === a.id ? "border-[var(--zc-accent)] bg-[var(--zc-accent-soft)] text-[var(--zc-accent)]" :
                photos[a.id] ? "border-[var(--zc-success)] text-[var(--zc-success)]" : "border-[var(--zc-border)] text-[var(--zc-text-3)]")}
          >
            {photos[a.id] ? <Check size={9} className="inline mr-0.5" /> : null}
            {a.label}
          </button>
        ))}
      </div>

      <div className="zc-card p-4">
        <div className="font-semibold text-sm">{current?.label}</div>
        <div className="text-xs text-[var(--zc-text-3)] mt-1">{current?.hint}</div>
        {showTripFields && (
          <p className="text-xs text-[var(--zc-text-3)] mt-2">
            Live capture required — take photos now at handover. Gallery uploads are not accepted for trip inspections.
          </p>
        )}

        {photos[activeAngle] && (
          <div className="mt-3 relative">
            <img src={photos[activeAngle].url} alt="" className="w-full max-h-[min(70vh,480px)] object-contain rounded-lg border border-[var(--zc-border)] bg-[var(--zc-bg-2)]" />
            <button
              type="button"
              onClick={() => setPhotos(prev => { const n = { ...prev }; delete n[activeAngle]; return n; })}
              className="absolute top-2 right-2 text-xs px-2 py-1 bg-white rounded shadow flex items-center gap-1"
            >
              <RotateCcw size={10} /> Retake
            </button>
          </div>
        )}

        {!photos[activeAngle] && (
          <label className="mt-4 flex flex-col items-center justify-center gap-2 w-full h-32 rounded-xl border-2 border-dashed border-[var(--zc-border)] cursor-pointer active:bg-[var(--zc-bg-2)]">
            <Camera size={22} className="text-[var(--zc-accent)]" />
            <span className="text-sm font-semibold">Take photo</span>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={e => uploadFile(e.target.files?.[0])}
              disabled={busy}
            />
          </label>
        )}

        {photos[activeAngle] && (
          <label className="mt-3 block text-center text-xs text-[var(--zc-accent)] font-semibold cursor-pointer">
            Replace photo
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={e => uploadFile(e.target.files?.[0])} disabled={busy} />
          </label>
        )}
      </div>

      {showTripFields && (
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div>
            <label className="text-xs text-[var(--zc-text-3)]">Odometer (km){isPickup ? " *" : ""}</label>
            <input className="zc-input w-full h-10 px-3 text-sm mt-1" value={odometer} onChange={e => setOdometer(e.target.value)} inputMode="numeric" placeholder="e.g. 20000" required={isPickup} />
            <div className="zc-meta mt-1">Clear odometer reading in km</div>
          </div>
          <div>
            <label className="text-xs text-[var(--zc-text-3)]">Fuel / battery %{isPickup ? " *" : ""}</label>
            <input className="zc-input w-full h-10 px-3 text-sm mt-1" value={fuel} onChange={e => setFuel(e.target.value)} inputMode="numeric" placeholder="75" required={isPickup} />
          </div>
        </div>
      )}

      <textarea
        className="zc-input w-full p-3 text-sm mt-3 h-16"
        value={notes}
        onChange={e => setNotes(e.target.value)}
        placeholder="Optional notes…"
      />

      {isListingDraft ? (
        <div
          className="fixed inset-x-0 z-40 px-4 pt-3 bg-[var(--zc-bg)] border-t border-[var(--zc-border)]"
          style={{ bottom: "calc(1rem + env(safe-area-inset-bottom))" }}
        >
          <p className="text-xs text-center text-[var(--zc-text-2)] max-w-md mx-auto">
            {photosCompleteForSession(session, photos)
              ? session?.phase === "parking_listing"
                ? "Photos captured — return to your browser to submit the parking listing."
                : "All angles captured — return to your browser to submit the listing."
              : session?.phase === "parking_listing"
                ? "Add at least one photo, then finish submitting on your computer."
                : "Capture all 6 angles, then finish submitting on your computer."}
          </p>
        </div>
      ) : (
        <div
          className="fixed inset-x-0 z-40 px-4"
          style={{ bottom: "calc(1rem + env(safe-area-inset-bottom))" }}
        >
        <button
          type="button"
          onClick={submit}
          disabled={submitting || !photosCompleteForSession(session, photos)}
          className="w-full max-w-md mx-auto h-12 zc-btn-primary text-sm flex items-center justify-center gap-2 shadow-lg"
          data-testid="mobile-inspect-submit"
        >
          {submitting ? <Loader size={14} className="animate-spin" /> : <Check size={14} />}
          Submit inspection
        </button>
        </div>
      )}
    </div>
  );
}
